import { CrmText } from "../CrmText";
import { registerComponent } from "@core/Plugins/pluginManager";
import { coreUiComponentDescriptions } from "@pluginShared/core-ui-api";
import { CSSProperties } from "react";
import { ArrayValuesViewer } from "../ArrayComponents/ArrayValuesViewer/ArrayValuesViewer";
import { InputValidator } from "@core/VisualComponents/Inputs/InputValidator/InputValidator";
import { t } from "i18next";
import { OrderListViewItemValueError } from "./OrderListViewItemValueError";
import { FormattedDate } from "../FormattedDate/FormattedDate";
import { CommentsToHtml, extractRootStyle } from "../CrmGridView/CrmCellInternal/CrmCellInternal";
import { useAppSelector } from "@core/Redux/hooks";
import { selectUserInfo } from "@core/Redux/store";
import styles from "./OrdersListViewItemValue.module.scss";
import { ICrmField, CrmFieldViewType } from "@core/Models/autogenerated/tenantConfig.models";
import { TagsViewer } from "../Tags/TagsViewer";

export interface IOrdersListViewItemValueProps {
    tableId: string;
    value: any,
    field: ICrmField,
    style?: CSSProperties;
}

export const OrdersListViewItemValue = registerComponent(coreUiComponentDescriptions.OrdersListViewItemValue, _OrdersListViewItemValue);

function _OrdersListViewItemValue(props: IOrdersListViewItemValueProps): JSX.Element {
    const { value, field } = props;
    const userInfo = useAppSelector(selectUserInfo);

    if (value == null || value === "") {
        return <span></span>;
    }

    switch (field.viewType) {
        case CrmFieldViewType.Date:
        case CrmFieldViewType.DateTime:
            if (InputValidator.validateByField(value, field)) {
                return <FormattedDate style={props.style} value={value} withTime={field.viewType == CrmFieldViewType.DateTime}/>
            }
            break;
        case CrmFieldViewType.Comments:
        case CrmFieldViewType.Timeline:
            const commentsStyleRaw = (props.field.style as any ?? {})[".commentsCell"] ;
            let commentsStyle = extractRootStyle(commentsStyleRaw);

            return <div className={styles.comments} style={commentsStyle}>{CommentsToHtml(value, userInfo)}</div>
        case CrmFieldViewType.Combobox:
            if (InputValidator.validateByField(value, field)) {
                return <CrmText style={props.style} text={
                    props.field.options!.find(x => x.value == value)?.label ?? value.toString()
                }/>;
            }
            break;
        case CrmFieldViewType.MultiString:
            if (typeof value != "string" && typeof value != "number") {
                break;
            }
            const str = (value?.toString() ?? '') as string
            let strArr = str.split('\n');
            if(strArr.length > 2){
                strArr = strArr.slice(0,2);
                strArr.push('...');
            }

            let html = [];
            let innerKey = 0;
            for (let i = 0; i < strArr.length; i++) {
                if (i > 0)
                    html.push(<br key={innerKey++}/>);
                html.push(<CrmText key={innerKey++} text={strArr[i]}/>)
            }
            return <span title={str}>{html}</span>;
        case CrmFieldViewType.Unknown:
        case CrmFieldViewType.String:
        case CrmFieldViewType.Phone:
            if (typeof value == "string" || typeof value == "number") {
                return <CrmText text={value.toString()} style={props.style}/>;
            }
            break;
        case CrmFieldViewType.Url:
            if (InputValidator.validateByField(value, field)) {
                return <CrmText text={value.toString()} style={props.style}/>;
            }
            break;
        case CrmFieldViewType.Decimal:
            if (InputValidator.validateByField(value, field)) {
                return <CrmText text={value.toString()} style={props.style}/>;
            }
            break;
        case CrmFieldViewType.Time:
            if (InputValidator.validateByField(value, field)) {
                return <CrmText text={value.toString()} style={props.style}/>;
            }
            break;
        case CrmFieldViewType.Array:
            if (Array.isArray(value)) {
                return <ArrayValuesViewer tableId={props.tableId} values={value} field={props.field} reversed/>
            }
            break;
        case CrmFieldViewType.YesNo:
            if (InputValidator.validateByField(value, field)) {
                return <CrmText style={props.style} text={
                    typeof value == "boolean"
                        ? (value ? t("yes") : t("no"))
                        : value.toString()
                }/>
            }
            break;
        case CrmFieldViewType.Tags:
            if (Array.isArray(value) && value.every(v => typeof v == "string")) {
                return <TagsViewer values={value} options={field.options}/>
            }
            break;
        default:
            break;
    }
    
    return <OrderListViewItemValueError value={value}/>;
}
